/**
 * Undo/Redo MVI Store
 *
 * Architecture: MVI Pattern - Store Layer
 * Pinia store orchestrating intents, reducer, and effects.
 * changelog-undo-redo-undo-redo-R31/R16; frontend-access-map-R01.
 */

import { defineStore } from "pinia";
import { ref, computed } from "vue";
import type { IApiError } from "@/shared/types";
import type { IUndoRedoState } from "./state";
import {
  createInitialState,
  selectCanUndo,
  selectCanRedo,
  selectHasSession,
  selectIsUndoRedoOffered,
  selectLastActionVersion,
  selectPendingFailure,
} from "./state";
import type { TUndoRedoIntent } from "./intents";
import { undoRedoReducer } from "./reducer";
import {
  initSession,
  closeSession,
  executeUndo,
  executeRedo,
  registerKeyboardShortcuts,
  unregisterKeyboardShortcuts,
  refreshUndoRedoState,
} from "./effects";

// ============================================================================
// Отказы, различимые без обращения к продукту
// ============================================================================

/**
 * «Нечего отменять» и «нечего возвращать» (undo-redo-R01, R02, R19).
 *
 * Признак причины тот же, что присылает продукт: пользователь видит один и тот
 * же текст независимо от того, узнал ли продукт о пустом стеке раньше клиента
 * (api-errors-R06).
 */
const NOTHING_TO_UNDO: IApiError = {
  code: "NOTHING_TO_UNDO",
  message: "Nothing to undo",
};

const NOTHING_TO_REDO: IApiError = {
  code: "NOTHING_TO_REDO",
  message: "Nothing to redo",
};

/**
 * Фича не выбрана: отменять нечего, потому что рабочего контекста нет
 * (business-model-versioning-R21, основание: вердикт 232).
 */
const FEATURE_NOT_SELECTED: IApiError = {
  code: "FEATURE_NOT_SELECTED",
  message: "No feature selected",
};

// ============================================================================
// Store
// ============================================================================

export const useUndoRedoStore = defineStore("undoRedo", () => {
  // State
  const state = ref<IUndoRedoState>(createInitialState());

  // Dispatch: applies intent through reducer
  function dispatch(intent: TUndoRedoIntent): void {
    state.value = undoRedoReducer(state.value, intent);
  }

  // Selectors (computed)
  const canUndo = computed(() => selectCanUndo(state.value));
  const canRedo = computed(() => selectCanRedo(state.value));
  const undoPrivilegeGranted = computed(() => state.value.domain.undoPrivilegeGranted);
  const redoPrivilegeGranted = computed(() => state.value.domain.redoPrivilegeGranted);
  const hasSession = computed(() => selectHasSession(state.value));
  /**
   * Видимость блока задаёт сеанс выбранного проекта (undo-redo-R11).
   * Фича и привилегии отдельно определяют доступность действий (R16, R31,
   * versioning-R21).
   */
  const isUndoRedoOffered = computed(() => selectIsUndoRedoOffered(state.value));
  const isProcessing = computed(() => state.value.ui.isProcessing);
  const isInitializing = computed(() => state.value.ui.isInitializing);
  const lastActionVersion = computed(() => selectLastActionVersion(state.value));
  /** Отказ, ожидающий показа пользователю (undo-redo-R19). */
  const pendingFailure = computed(() => selectPendingFailure(state.value));

  // ========================================================================
  // Public Actions (delegate to effects)
  // ========================================================================

  /**
   * Initialize edit session and keyboard shortcuts.
   *
   * Клавиши подписываются с проверкой доступности: сеанс не поднялся — кнопок
   * нет, и клавиши не действуют (undo-redo-R16).
   */
  async function init(projectId: number): Promise<void> {
    setProjectSelected(projectId);
    await initSession(projectId, dispatch);

    registerKeyboardShortcuts(
      () => { void undo(); },
      () => { void redo(); },
      () => selectIsUndoRedoOffered(state.value) && selectCanUndo(state.value),
      () => selectIsUndoRedoOffered(state.value) && selectCanRedo(state.value),
    );
  }

  /**
   * Perform undo. Returns affected entity kinds for selective refetch.
   *
   * Отмена, которой нет чего отменять, — отказ, а не тишина: пользователь
   * обязан узнать, почему ничего не изменилось (undo-redo-R19). Отмену, которую
   * продукт не предлагает вовсе, здесь и не пытаются выполнить (R16).
   */
  async function undo(): Promise<readonly string[] | null> {
    if (!selectIsUndoRedoOffered(state.value)) return null;
    if (!state.value.domain.undoPrivilegeGranted) return null;
    // Сеанс есть, а фича не выбрана — действие заблокировано, но попытка
    // (например, по горячей клавише из раздела без выбора) сообщает причину,
    // а не молчит (versioning-R21, undo-redo-R19).
    if (!state.value.domain.hasSelectedFeature) {
      dispatch({ type: "UndoFailed", error: FEATURE_NOT_SELECTED });
      return null;
    }
    if (!state.value.domain.isFeatureOfSelectedProject) return null;
    // Операция уже идёт — повторное нажатие не второй запрос и не отказ.
    if (state.value.ui.isProcessing) return null;

    if (!state.value.domain.canUndo) {
      dispatch({ type: "UndoFailed", error: NOTHING_TO_UNDO });
      return null;
    }

    const result = await executeUndo(dispatch);
    return result?.affectedEntityKinds ?? null;
  }

  /** Perform redo. Returns affected entity kinds for selective refetch. */
  async function redo(): Promise<readonly string[] | null> {
    if (!selectIsUndoRedoOffered(state.value)) return null;
    if (!state.value.domain.redoPrivilegeGranted) return null;
    if (!state.value.domain.hasSelectedFeature) {
      dispatch({ type: "RedoFailed", error: FEATURE_NOT_SELECTED });
      return null;
    }
    if (!state.value.domain.isFeatureOfSelectedProject) return null;
    if (state.value.ui.isProcessing) return null;

    if (!state.value.domain.canRedo) {
      dispatch({ type: "RedoFailed", error: NOTHING_TO_REDO });
      return null;
    }

    const result = await executeRedo(dispatch);
    return result?.affectedEntityKinds ?? null;
  }

  /** Отказ показан — снять его с ожидания показа. */
  function failureShown(): void {
    dispatch({ type: "FailureShown" });
  }

  /** R11/R16: выбор меняется до завершения сетевого закрытия прежнего сеанса. */
  function setProjectSelected(projectId: number | null): void {
    dispatch({ type: "ProjectSelectionChanged", projectId });
  }

  /** Update canUndo/canRedo from external source (e.g. response headers) */
  /**
   * Сообщить, выбрана ли фича в рабочем контексте (versioning-R21) и
   * принадлежит ли она проекту сеанса (undo-redo-R11, R16).
   *
   * Отмена и возврат недоступны без выбранной фичи, а также — защитно — там,
   * где показано содержимое чужого для сеанса проекта: штатно такого
   * состояния нет (business-model-features-R10, DEC-1433), а правки чужого
   * проекта к сеансу не привязываются (changelog-R14).
   */
  function setFeatureSelected(
    hasSelectedFeature: boolean,
    belongsToSelectedProject = true,
  ): void {
    dispatch({
      type: "FeatureSelectionChanged",
      hasSelectedFeature,
      belongsToSelectedProject,
    });
  }

  function updateState(
    canUndoNew: boolean,
    canRedoNew: boolean,
    undoPrivilegeGrantedNew = false,
    redoPrivilegeGrantedNew = false,
  ): void {
    dispatch({
      type: "StateUpdated",
      canUndo: canUndoNew,
      canRedo: canRedoNew,
      undoPrivilegeGranted: undoPrivilegeGrantedNew,
      redoPrivilegeGranted: redoPrivilegeGrantedNew,
    });
  }

  /**
   * Группа отменена или возвращена операцией над группой по её
   * идентификатору, а не из личного стека (undo-redo-R20, R12).
   *
   * Признаки кнопок берутся у сервера, а не из ответа операции: он отдаёт
   * состояние сеанса **той** группы, а личный стек текущей вкладки чужая
   * группа не трогает (undo-redo-R10).
   */
  async function applyExternalGroupChange(): Promise<void> {
    if (selectHasSession(state.value)) {
      await refreshUndoRedoState(dispatch);
    }

    dispatch({
      type: "ExternalGroupApplied",
      canUndo: state.value.domain.canUndo,
      canRedo: state.value.domain.canRedo,
    });
  }

  /**
   * Закрыть сеанс и снять подписки.
   *
   * Клиент закрывает сеанс, **покидая проект**: при сбросе проекта, при смене
   * проекта и при уходе со страницы (edit-sessions-R03, R04). Смена проекта
   * начинает новый сеанс с пустыми стеками — накопленное прежним ему не
   * переходит (R13), но из аудит-лога не исчезает: в интерфейсе оно не
   * отменяется, а исправляется новой правкой (undo-redo-R25).
   */
  async function destroy(): Promise<void> {
    unregisterKeyboardShortcuts();
    await closeSession(dispatch);
  }

  return {
    // State (readonly)
    state,
    // Selectors
    canUndo,
    canRedo,
    undoPrivilegeGranted,
    redoPrivilegeGranted,
    hasSession,
    isUndoRedoOffered,
    setProjectSelected,
    isProcessing,
    isInitializing,
    lastActionVersion,
    pendingFailure,
    // Actions
    init,
    undo,
    redo,
    failureShown,
    setFeatureSelected,
    updateState,
    applyExternalGroupChange,
    destroy,
  };
});
